import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { DASHBOARD_PATH } from "@/constants/constant";
import { ApiError } from "@/services/api/auth/auth.type";
import { useClaimCampaignsApiCallMutation } from "@/services/api/campaigns/campaigns.api";
import { useGetTransactionsApiCallQuery } from "@/services/api/transactions/transactions.api";
import { getTimeRemaining } from "@/utils/getTimeRemaining";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { ArrowRight, Loader2 } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Loader from "@/components/Loader";

const ActiveCampaign = () => {
  const {
    data: transactionsData,
    isError: transactionsError,
    isLoading,
  } = useGetTransactionsApiCallQuery({
    transactionType: "JOINED",
  });

  const [claimRewardsCall, { isLoading: isClaiming }] =
    useClaimCampaignsApiCallMutation();

  const claimRewards = async (id: string) => {
    console.log(id);
    try {
      const response = await claimRewardsCall({
        campaignId: id,
      }).unwrap();

      if (response?.responseCode === 200) {
        toast.success(response?.responseMessage);
      } else {
        toast.error(response?.responseMessage);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Error claiming rewards");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader size="lg" text="Loading active campaigns..." />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <h2 className="text-xl md:text-2xl text-white">My Active Campaigns</h2>
        {/* <Link to={DASHBOARD_PATH?.ACTIVE_CAMPAIGNS}>
          <Button
            variant="outline"
            className="cursor-pointer border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-black mt-4 md:mt-0"
          >
            View All
          </Button>
        </Link> */}
      </div>

      <div>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
          {transactionsData?.result?.docs &&
            transactionsData?.result?.docs?.length > 0 &&
            transactionsData?.result?.docs.map((campaign) => (
              <Card
                key={campaign._id}
                className="bg-gray-900/50 border-yellow-500/20 hover:border-yellow-500/40 transition-colors"
              >
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-white text-base md:text-lg">
                      {campaign.campaignId?.name}
                    </h3>
                    {campaign.isCompleted && !campaign.isClaimed ? (
                      <Badge className="bg-green-500 text-white">
                        Ready to Claim
                      </Badge>
                    ) : (
                      <Badge className="bg-blue-500 text-white">
                        In Progress
                      </Badge>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Progress</span>
                      <span className="text-white">
                        {campaign.allVerifiedTasks}/{campaign.allTasks} tasks
                      </span>
                    </div>
                    <Progress
                      value={
                        (campaign.allVerifiedTasks / campaign.allTasks) * 100
                      }
                      className="h-2 bg-gray-800"
                    >
                      <div
                        className="h-full bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full transition-all"
                        style={{
                          width: `${
                            (campaign.allVerifiedTasks / campaign.allTasks) *
                            100
                          }%`,
                        }}
                      />
                    </Progress>
                  </div>

                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-white">
                        {campaign.totalReward} BINGOLD
                      </div>
                      <div className="text-gray-400 text-sm">
                        Potential Reward
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-white">
                        {
                          getTimeRemaining(
                            campaign.campaignId?.startDate,
                            campaign.campaignId?.endDate,
                          ).days
                        }{" "}
                        day
                      </div>
                      <div className="text-gray-400 text-sm">Time Left</div>
                    </div>
                  </div>

                  {campaign.isCompleted && !campaign?.isClaimed ? (
                    <Button
                      onClick={() => claimRewards(campaign.campaignId?._id)}
                      disabled={isClaiming}
                      className={`w-full cursor-pointer
                             bg-gradient-to-r from-green-400 to-green-600 text-black hover:from-green-500 hover:to-green-700`}
                    >
                      {isClaiming ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />{" "}
                          Claiming...
                        </>
                      ) : (
                        <>
                          Claim Rewards
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  ) : (
                    <Link
                      to={`${DASHBOARD_PATH.INDIVIDUAL_CAMPAIGN}/${campaign.campaignId?._id}`}
                    >
                      <Button
                        className={`w-full cursor-pointer 
                           bg-gradient-to-r from-yellow-400 to-yellow-600 text-black hover:from-yellow-500 hover:to-yellow-700
                      }`}
                      >
                        Continue Tasks
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            ))}
        </div>

        {(transactionsError ||
          transactionsData?.result?.docs?.length === 0) && (
          <div>
            <p>No Active Campaigns Found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActiveCampaign;
